import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/react';
import {
  Paper,
  Typography,
  List,
  ListItemButton,
  Divider,
  Skeleton,
} from '@mui/material';
import { Box, BoxProps } from '@mui/system';
import { ClubLogo, useItems } from '@scorenco/components';
import { RouteLink, SsrQueryArgs } from '@scorenco/core';

import { RouteKey } from '@/routes';

import { GetClubsOfDepartmentDocument } from './Clubs.query.generated';

type ClubListProps = BoxProps & {
  sportSlug: string;
  departmentCode: string;
};

export const ClubList = ({
  sportSlug,
  departmentCode,
  ...rest
}: ClubListProps) => {
  const clubsDepartmentQuery = useQuery(GetClubsOfDepartmentDocument, {
    context: {
      role: 'anonymous',
    },
    variables: {
      sportSlug,
      departmentCode,
    },
  });
  const clubs = useItems(clubsDepartmentQuery.data?.clubs_club, 10);

  return (
    <Box {...rest}>
      <Typography
        variant="h5"
        sx={{ textTransform: 'uppercase', fontStyle: 'unset', mb: 2 }}
      >
        {clubs.length} clubs
      </Typography>

      <Paper variant="bordered" sx={{ padding: 0, alignItems: 'stretch' }}>
        <List disablePadding>
          {clubs.map((club, idx) => (
            <>
              {idx !== 0 && <Divider key={`divider-${club?.id || idx}`} />}
              <ListItemButton
                key={`link-${club?.id || idx}`}
                component={RouteLink}
                href={
                  club
                    ? {
                        routeKey: RouteKey.Club,
                        params: {
                          sport: sportSlug,
                          club: `${club?.slug}-${club?.id.toString(36)}`,
                        },
                      }
                    : undefined
                }
              >
                <ClubLogo
                  logo={club?.logo}
                  css={css`
                    width: 2.5rem;
                    height: 2.5rem;
                    margin-right: 1rem;
                  `}
                  width={40}
                  height={40}
                />
                {club?.name || <Skeleton width={200} />}
              </ListItemButton>
            </>
          ))}
        </List>
      </Paper>
    </Box>
  );
};

ClubList.ssrQuery = async ({
  apolloClient,
  variables,
}: SsrQueryArgs & {
  variables: {
    sportSlug: string;
    departmentCode: string;
  };
}) =>
  await apolloClient.query({
    query: GetClubsOfDepartmentDocument,
    variables,
  });
